import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaExpand,
  FaCompress,
  FaCog,
  FaBackward,
  FaForward,
  FaClosedCaptioning,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaInfoCircle,
  FaList,
  FaTimes,
} from "react-icons/fa";

const PLAYER_THEME_STORAGE_KEY = "lip_player_theme";

function toPlayableVideoUrl(url) {
  if (!url) return "";

  try {
    const raw = url.trim();
    const u = new URL(raw);
    const hostname = u.hostname.replace(/^www\./, "").toLowerCase();
    const pathname = u.pathname.toLowerCase();

    const isDropbox =
      hostname === "dropbox.com" || hostname === "dl.dropboxusercontent.com";

    if (isDropbox) {
      u.hostname = "dl.dropboxusercontent.com";
      u.searchParams.delete("dl");
      u.searchParams.delete("raw");
      return u.toString();
    }

    const isGoogleDrive = hostname === "drive.google.com";

    if (isGoogleDrive) {
      const fileMatch = u.pathname.match(/\/file\/d\/([^/]+)/);
      if (fileMatch?.[1]) {
        const fileId = fileMatch[1];
        return `/api/google-allow?id=${encodeURIComponent(fileId)}`;
      }

      const id = u.searchParams.get("id");
      if (id) {
        return `/api/google-allow?id=${encodeURIComponent(id)}`;
      }

      throw new Error("Unsupported Google Drive link format.");
    }

    const isAlreadyProxied =
      raw.startsWith("/api/video-proxy") || raw.includes("/api/video-proxy?");

    if (isAlreadyProxied) {
      return raw;
    }

    const isCloudflareCdn =
      hostname === "cdn.lightideaproductions.co.uk" ||
      hostname.endsWith(".r2.dev");

    if (isCloudflareCdn) {
      return `/api/video-proxy?url=${encodeURIComponent(raw)}`;
    }

    const isMp4 = pathname.endsWith(".mp4") || pathname.includes(".mp4");

    if (isMp4) {
      return `/api/video-proxy?url=${encodeURIComponent(raw)}`;
    }

    return raw;
  } catch {
    return url;
  }
}

function formatSeasonDisplay(season) {
  if (season?.season_label?.trim()) return season.season_label.trim();
  if (season?.season_number !== null && season?.season_number !== undefined) {
    return `Season ${season.season_number}`;
  }
  return "Untitled Season";
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return "00:00";
  const total = Math.max(0, Math.floor(seconds));
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0",
    )}`;
  }

  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const PLAYER_THEMES = {
  anger: {
    name: "Anger",
    vars: {
      "--shell-from": "#180303",
      "--shell-via": "#2a0707",
      "--shell-to": "#0f0202",
      "--frame-shadow": "0 0 90px rgba(255,59,59,0.28)",
      "--accent-from": "#ff1f1f",
      "--accent-mid": "#ff3b3b",
      "--accent-to": "#b30000",
      "--accent-soft": "rgba(239,68,68,0.20)",
      "--accent-border": "rgba(252,165,165,0.30)",
      "--header-bg": "rgba(76,1,1,0.95)",
      "--footer-bg": "#4c0101",
    },
  },
  cinematic: {
    name: "Cinematic",
    vars: {
      "--shell-from": "#05020c",
      "--shell-via": "#0a0416",
      "--shell-to": "#030109",
      "--frame-shadow": "0 0 90px rgba(114,66,255,0.26)",
      "--accent-from": "#6b34ff",
      "--accent-mid": "#9a7cff",
      "--accent-to": "#e12fff",
      "--accent-soft": "rgba(123,77,255,0.25)",
      "--accent-border": "rgba(162,127,255,0.40)",
      "--header-bg": "rgba(19,7,32,0.95)",
      "--footer-bg": "#130720",
    },
  },
  cool: {
    name: "Cool",
    vars: {
      "--shell-from": "#03101c",
      "--shell-via": "#07182c",
      "--shell-to": "#030b16",
      "--frame-shadow": "0 0 90px rgba(43,142,255,0.24)",
      "--accent-from": "#10b0ff",
      "--accent-mid": "#4388ff",
      "--accent-to": "#7c6cff",
      "--accent-soft": "rgba(14,165,233,0.20)",
      "--accent-border": "rgba(125,211,252,0.30)",
      "--header-bg": "rgba(3,25,42,0.95)",
      "--footer-bg": "#03192a",
    },
  },
};

export default function Show() {
  const { showId, episodeId } = useParams();
  const navigate = useNavigate();

  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const hideChromeTimer = useRef(null);
  const nextEpisodeTimer = useRef(null);
  const feedbackTimer = useRef(null);

  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [otherShows, setOtherShows] = useState([]);
  const [currentSeasonIndex, setCurrentSeasonIndex] = useState(0);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [error, setError] = useState("");

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [showChrome, setShowChrome] = useState(true);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showLibraryRail, setShowLibraryRail] = useState(false);
  const [railTab, setRailTab] = useState("episodes");
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [themeKey, setThemeKey] = useState(() => {
    const savedTheme =
      typeof window !== "undefined"
        ? window.localStorage.getItem(PLAYER_THEME_STORAGE_KEY)
        : null;

    return PLAYER_THEMES[savedTheme] ? savedTheme : "anger";
  });
  const [feedbackText, setFeedbackText] = useState("");
  const [showNextEpisodeCard, setShowNextEpisodeCard] = useState(false);
  const [nextCountdown, setNextCountdown] = useState(12);
  const [showCaptionNotice, setShowCaptionNotice] = useState(true);
  const [captionNoticeFading, setCaptionNoticeFading] = useState(false);

  const theme = PLAYER_THEMES[themeKey] || PLAYER_THEMES.anger;
  const themeVars = useMemo(() => theme.vars || {}, [theme]);

  useEffect(() => {
    loadShow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showId, episodeId]);

  useEffect(() => {
    const selectedTheme = PLAYER_THEMES[themeKey];
    if (!selectedTheme) return;

    window.localStorage.setItem(PLAYER_THEME_STORAGE_KEY, themeKey);

    Object.entries(selectedTheme.vars || {}).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    window.dispatchEvent(
      new CustomEvent("player-theme-change", {
        detail: { themeKey },
      }),
    );
  }, [themeKey]);

  const getPublicUrl = useCallback((path) => {
    if (!path) return "";
    const { data } = supabase.storage.from("media").getPublicUrl(path);
    return data?.publicUrl || "";
  }, []);

  const currentSeason = useMemo(
    () => seasons[currentSeasonIndex] || null,
    [seasons, currentSeasonIndex],
  );

  const allEpisodesFlat = useMemo(() => {
    return seasons.flatMap((season, seasonIndex) =>
      (season.episodes || []).map((episode, episodeIndex) => ({
        ...episode,
        __seasonIndex: seasonIndex,
        __episodeIndex: episodeIndex,
      })),
    );
  }, [seasons]);

  const currentEpisodeFlatIndex = useMemo(() => {
    if (!currentEpisode) return -1;
    return allEpisodesFlat.findIndex((ep) => ep.id === currentEpisode.id);
  }, [allEpisodesFlat, currentEpisode]);

  const previousEpisode = useMemo(() => {
    if (currentEpisodeFlatIndex <= 0) return null;
    return allEpisodesFlat[currentEpisodeFlatIndex - 1] || null;
  }, [allEpisodesFlat, currentEpisodeFlatIndex]);

  const nextEpisode = useMemo(() => {
    if (currentEpisodeFlatIndex < 0) return null;
    return allEpisodesFlat[currentEpisodeFlatIndex + 1] || null;
  }, [allEpisodesFlat, currentEpisodeFlatIndex]);

  const videoUrl = useMemo(() => {
    if (!currentEpisode) return "";
    if (currentEpisode.video_url) {
      return toPlayableVideoUrl(currentEpisode.video_url);
    }
    if (currentEpisode.video_path) {
      return getPublicUrl(currentEpisode.video_path);
    }
    return "";
  }, [currentEpisode, getPublicUrl]);

  const episodeThumbUrl = useMemo(() => {
    if (!currentEpisode) return "";
    if (currentEpisode.thumbnail_path) {
      return getPublicUrl(currentEpisode.thumbnail_path);
    }
    return "";
  }, [currentEpisode, getPublicUrl]);

  const captionUrl = useMemo(() => {
    if (!currentEpisode) return "";
    if (currentEpisode.caption_path) {
      return getPublicUrl(currentEpisode.caption_path);
    }
    return "";
  }, [currentEpisode, getPublicUrl]);

  const showPosterUrl = useMemo(() => {
    if (!show?.hero_image_path) return "";
    return getPublicUrl(show.hero_image_path);
  }, [show, getPublicUrl]);

  const otherShowsMapped = useMemo(() => {
    return (otherShows || []).map((item) => ({
      ...item,
      hero_image_url: item.hero_image_path
        ? getPublicUrl(item.hero_image_path)
        : "",
    }));
  }, [otherShows, getPublicUrl]);

  const progressPercent = useMemo(
    () => (duration ? (currentTime / duration) * 100 : 0),
    [currentTime, duration],
  );

  const bufferedPercent = useMemo(
    () => (duration ? (bufferedEnd / duration) * 100 : 0),
    [bufferedEnd, duration],
  );

  const remainingTime = Math.max(0, (duration || 0) - currentTime);

  const currentEpisodeMeta = useMemo(() => {
    if (!currentEpisode) return "";
    const seasonText =
      currentSeason?.season_number !== null &&
      currentSeason?.season_number !== undefined
        ? `Series ${currentSeason.season_number}`
        : formatSeasonDisplay(currentSeason);

    return `${seasonText} • Episode ${currentEpisode?.episode_number || 1}`;
  }, [currentEpisode, currentSeason]);

  const showFeedback = useCallback((text) => {
    clearTimeout(feedbackTimer.current);
    setFeedbackText(text);
    feedbackTimer.current = setTimeout(() => setFeedbackText(""), 850);
  }, []);

  const revealChrome = useCallback(() => {
    clearTimeout(hideChromeTimer.current);
    setShowChrome(true);

    if (isPlaying) {
      hideChromeTimer.current = setTimeout(() => {
        setShowChrome(false);
        setShowSettings(false);
        setShowInfo(false);
        setShowLibraryRail(false);
      }, 2800);
    }
  }, [isPlaying]);

  const clearNextEpisodeCountdown = useCallback(() => {
    setShowNextEpisodeCard(false);
    setNextCountdown(12);

    if (nextEpisodeTimer.current) {
      clearInterval(nextEpisodeTimer.current);
      nextEpisodeTimer.current = null;
    }
  }, []);

  const playEpisode = useCallback(
    (episode, seasonIndexOverride = null, autoplay = true) => {
      if (!episode) return;

      const nextSeasonIndex =
        seasonIndexOverride !== null
          ? seasonIndexOverride
          : seasons.findIndex((season) => season.id === episode.season_id);

      clearNextEpisodeCountdown();
      navigate(`/show/${showId}/${episode.id}`, { replace: false });

      setCurrentSeasonIndex(nextSeasonIndex >= 0 ? nextSeasonIndex : 0);
      setCurrentEpisode(episode);
      setCurrentTime(0);
      setDuration(0);
      setBufferedEnd(0);
      setIsLoadingVideo(true);
      setShowInfo(false);
      setShowSettings(false);
      setShowLibraryRail(false);
      setCaptionsEnabled(false);

      requestAnimationFrame(() => {
        const video = videoRef.current;
        if (!video) return;

        video.load();

        if (autoplay) {
          try {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === "function") {
              playPromise.catch(() => {});
            }
          } catch {}
        }
      });
    },
    [clearNextEpisodeCountdown, navigate, seasons, showId],
  );

  const goToPreviousEpisode = useCallback(() => {
    if (!previousEpisode) return;
    playEpisode(previousEpisode, previousEpisode.__seasonIndex, true);
  }, [playEpisode, previousEpisode]);

  const goToNextEpisode = useCallback(() => {
    if (!nextEpisode) return;
    playEpisode(nextEpisode, nextEpisode.__seasonIndex, true);
  }, [nextEpisode, playEpisode]);

  async function loadShow() {
    setLoading(true);
    setError("");

    try {
      const { data: showData, error: showErr } = await supabase
        .from("shows")
        .select("*")
        .eq("id", showId)
        .single();

      if (showErr) throw showErr;

      const { data: seasonsData, error: seasonsErr } = await supabase
        .from("seasons")
        .select("*")
        .eq("show_id", showId)
        .order("season_number", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });

      if (seasonsErr) throw seasonsErr;

      const seasonIds = (seasonsData || []).map((s) => s.id);

      let episodesBySeason = {};
      if (seasonIds.length > 0) {
        const { data: episodesData, error: episodesErr } = await supabase
          .from("episodes")
          .select("*")
          .in("season_id", seasonIds)
          .order("episode_number", { ascending: true });

        if (episodesErr) throw episodesErr;

        episodesBySeason = (episodesData || []).reduce((acc, ep) => {
          acc[ep.season_id] = acc[ep.season_id] || [];
          acc[ep.season_id].push(ep);
          return acc;
        }, {});
      }

      const merged = (seasonsData || []).map((s) => ({
        ...s,
        episodes: episodesBySeason[s.id] || [],
      }));

      const { data: otherShowsData, error: otherShowsErr } = await supabase
        .from("shows")
        .select("*")
        .neq("id", showId)
        .order("created_at", { ascending: false });

      if (otherShowsErr) {
        console.warn("Other shows fetch error:", otherShowsErr.message);
      }

      setShow(showData);
      setSeasons(merged);
      setOtherShows(otherShowsData || []);

      const firstEp = merged?.[0]?.episodes?.[0] || null;

      let selectedEpisode = firstEp;
      let selectedSeasonIndex = 0;

      if (episodeId) {
        for (
          let seasonIndex = 0;
          seasonIndex < merged.length;
          seasonIndex += 1
        ) {
          const foundEpisode = (merged[seasonIndex].episodes || []).find(
            (ep) => String(ep.id) === String(episodeId),
          );

          if (foundEpisode) {
            selectedEpisode = foundEpisode;
            selectedSeasonIndex = seasonIndex;
            break;
          }
        }
      }

      setCurrentSeasonIndex(selectedSeasonIndex);
      setCurrentEpisode(selectedEpisode);
      setCaptionsEnabled(false);
    } catch (e) {
      setError(e.message || "Failed to load show.");
    } finally {
      setLoading(false);
    }
  }

  const togglePlay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    clearNextEpisodeCountdown();

    try {
      if (video.paused) {
        await video.play();
      } else {
        video.pause();
      }
    } catch (error) {
      console.error("Playback error", error);
    }
  }, [clearNextEpisodeCountdown]);

  const seekBy = useCallback(
    (amount) => {
      const video = videoRef.current;
      if (!video) return;

      const next = clamp(video.currentTime + amount, 0, duration || 0);
      video.currentTime = next;
      setCurrentTime(next);
      showFeedback(amount > 0 ? `+${amount}s` : `${amount}s`);
    },
    [duration, showFeedback],
  );

  const handleVolumeChange = (next) => {
    const video = videoRef.current;
    const safe = clamp(next, 0, 1);
    setVolume(safe);
    setIsMuted(safe === 0);

    if (video) {
      video.volume = safe;
      video.muted = safe === 0;
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setIsMuted(nextMuted);

    if (!nextMuted && video.volume === 0) {
      video.volume = 0.85;
      setVolume(0.85);
    }
  };

  const toggleFullscreen = async () => {
    const node = playerRef.current;
    if (!node) return;

    try {
      if (!document.fullscreenElement) {
        await node.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen error", error);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLoadedMetadata = () => {
      setDuration(video.duration || 0);
      setIsLoadingVideo(false);
      video.volume = volume;
      video.playbackRate = playbackRate;
    };

    const onTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(video.currentTime || 0);
      }

      if (video.buffered?.length) {
        try {
          setBufferedEnd(video.buffered.end(video.buffered.length - 1));
        } catch {
          setBufferedEnd(0);
        }
      }

      const remaining = (video.duration || 0) - (video.currentTime || 0);

      if (
        nextEpisode &&
        video.duration > 0 &&
        remaining <= 15 &&
        remaining > 0
      ) {
        setShowNextEpisodeCard(true);
      } else if (remaining > 15) {
        setShowNextEpisodeCard(false);
        setNextCountdown(12);
      }
    };

    const onPlay = () => {
      setIsPlaying(true);
      revealChrome();
    };

    const onPause = () => {
      setIsPlaying(false);
      setShowChrome(true);
      clearTimeout(hideChromeTimer.current);
    };

    const onVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted || video.volume === 0);
    };

    const onWaiting = () => setIsLoadingVideo(true);
    const onPlaying = () => setIsLoadingVideo(false);
    const onRateChange = () => setPlaybackRate(video.playbackRate || 1);

    const onEnded = () => {
      if (nextEpisode) {
        goToNextEpisode();
      }
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("volumechange", onVolumeChange);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("ratechange", onRateChange);
    video.addEventListener("ended", onEnded);

    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("volumechange", onVolumeChange);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("ratechange", onRateChange);
      video.removeEventListener("ended", onEnded);
    };
  }, [
    currentEpisode,
    isSeeking,
    nextEpisode,
    playbackRate,
    volume,
    revealChrome,
    goToNextEpisode,
  ]);

  useEffect(() => {
    clearInterval(nextEpisodeTimer.current);

    if (showNextEpisodeCard && isPlaying && nextEpisode) {
      nextEpisodeTimer.current = setInterval(() => {
        setNextCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(nextEpisodeTimer.current);
            goToNextEpisode();
            return 12;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(nextEpisodeTimer.current);
  }, [showNextEpisodeCard, isPlaying, nextEpisode, goToNextEpisode]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !video.textTracks) return;

    for (let i = 0; i < video.textTracks.length; i += 1) {
      video.textTracks[i].mode = captionsEnabled ? "showing" : "disabled";
    }
  }, [captionsEnabled, currentEpisode]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
      revealChrome();
    };

    const onKeyDown = (event) => {
      const active = document.activeElement?.tagName?.toLowerCase();
      if (active === "input" || active === "textarea") return;

      switch (event.key.toLowerCase()) {
        case " ":
        case "k":
          event.preventDefault();
          togglePlay();
          break;
        case "arrowleft":
        case "j":
          event.preventDefault();
          seekBy(-10);
          break;
        case "arrowright":
        case "l":
          event.preventDefault();
          seekBy(10);
          break;
        case "arrowup":
          event.preventDefault();
          handleVolumeChange((isMuted ? 0 : volume) + 0.05);
          break;
        case "arrowdown":
          event.preventDefault();
          handleVolumeChange((isMuted ? 0 : volume) - 0.05);
          break;
        case "m":
          event.preventDefault();
          toggleMute();
          break;
        case "f":
          event.preventDefault();
          toggleFullscreen();
          break;
        case "c":
          event.preventDefault();
          setCaptionsEnabled((prev) => !prev);
          break;
        case "i":
          event.preventDefault();
          setShowInfo((prev) => !prev);
          setShowLibraryRail(false);
          break;
        case "e":
          event.preventDefault();
          setShowLibraryRail((prev) => !prev);
          setShowInfo(false);
          break;
        default:
          break;
      }
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isMuted, volume, revealChrome, seekBy, togglePlay]);

  useEffect(() => {
    if (!currentEpisode) return;

    setShowCaptionNotice(true);
    setCaptionNoticeFading(false);
    setIsPlaying(false);
    setIsLoadingVideo(true);

    const fadeTimer = setTimeout(() => {
      setCaptionNoticeFading(true);
    }, 1800);

    const hideTimer = setTimeout(() => {
      setShowCaptionNotice(false);
    }, 2600);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [currentEpisode]);

  useEffect(() => {
    return () => {
      clearTimeout(hideChromeTimer.current);
      clearTimeout(feedbackTimer.current);
      clearInterval(nextEpisodeTimer.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05020c] text-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-lg">
          <FaSpinner className="animate-spin" />
          <span>Loading show...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#05020c] text-white p-6 sm:p-10">
        <p className="mb-3 text-2xl font-bold">Error</p>
        <p className="mb-6 text-gray-300">{error}</p>
        <button
          onClick={loadShow}
          className="rounded-xl bg-white px-6 py-3 font-semibold text-black"
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!show) {
    return (
      <div className="min-h-screen bg-[#05020c] p-6 text-white sm:p-10">
        Show not found.
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white transition-[background] duration-700 ease-in-out"
      style={{
        ...themeVars,
        backgroundImage:
          "linear-gradient(to bottom right, var(--shell-from), var(--shell-via), var(--shell-to))",
      }}
    >
      <div className="mx-auto flex min-h-screen w-full max-w-[1800px] items-center justify-center p-0 sm:p-3 md:p-4 xl:p-6 2xl:p-8">
        <div
          ref={playerRef}
          className="relative w-full overflow-hidden border border-white/10 bg-black
            min-h-screen sm:min-h-[220px]
            sm:max-h-[94vh]
            sm:aspect-[16/9]
            rounded-none sm:rounded-[24px] lg:rounded-[30px]
            transition-[box-shadow] duration-700 ease-in-out"
          style={{ boxShadow: "var(--frame-shadow)" }}
          onMouseMove={revealChrome}
          onMouseEnter={revealChrome}
          onTouchStart={revealChrome}
          onTouchMove={revealChrome}
        >
          {currentEpisode && videoUrl ? (
            <>
              <video
                ref={videoRef}
                key={currentEpisode.id}
                className="absolute inset-0 h-full w-full object-cover"
                src={videoUrl}
                poster={episodeThumbUrl || showPosterUrl || undefined}
                playsInline
                preload="metadata"
                crossOrigin="anonymous"
                onClick={togglePlay}
              >
                {captionUrl ? (
                  <track
                    kind="subtitles"
                    src={captionUrl}
                    srcLang="en"
                    label="English"
                    default
                  />
                ) : null}
              </video>

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/95 via-black/15 to-black/80" />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(143,94,255,0.25),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(0,149,255,0.18),transparent_30%)]" />

              {captionsEnabled ? (
                <div className="pointer-events-none absolute bottom-[112px] left-1/2 z-20 w-[min(94%,760px)] -translate-x-1/2 px-3 text-center sm:bottom-[118px] lg:bottom-[124px]">
                  <div className="inline-block rounded-xl bg-black/70 px-3 py-2 text-[11px] font-medium tracking-wide text-white shadow-xl backdrop-blur-sm sm:px-4 sm:text-sm">
                    {captionUrl
                      ? "Closed captions available for this episode."
                      : "Some episodes may not include closed captions."}
                  </div>
                </div>
              ) : null}

              {showCaptionNotice ? (
                <div
                  className={`pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-black transition-opacity duration-700 ${
                    captionNoticeFading ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <p className="px-6 text-center text-sm font-medium text-white sm:text-lg md:text-xl">
                    Some episodes may not include closed captions.
                  </p>
                </div>
              ) : null}

              {feedbackText ? (
                <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
                  <div className="rounded-full border border-white/10 bg-white/10 px-4 py-3 text-sm font-semibold shadow-2xl backdrop-blur-md sm:px-6 sm:py-4 sm:text-2xl">
                    {feedbackText}
                  </div>
                </div>
              ) : null}

              {isLoadingVideo ? (
                <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
                  <div className="rounded-2xl border border-white/10 bg-black/45 p-4 shadow-2xl backdrop-blur-md sm:p-5">
                    <FaSpinner className="h-8 w-8 animate-spin text-white sm:h-10 sm:w-10" />
                  </div>
                </div>
              ) : null}

              <div
                className={`absolute inset-0 z-20 transition-opacity duration-300 ${
                  showChrome || !isPlaying ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="absolute inset-x-0 top-0 z-20 p-3 sm:p-4 lg:p-6">
                  <div className="mx-auto flex max-w-[calc(100%-8px)] items-start justify-between gap-4">
                    <div className="min-w-0 max-w-4xl">
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-[9px] uppercase tracking-[0.18em] text-white/60 sm:text-[11px] sm:tracking-[0.28em]">
                        <span
                          className="rounded-full px-2.5 py-1 text-white/90 backdrop-blur-md sm:px-3"
                          style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
                        >
                          Light Idea Productions
                        </span>
                        <span>{currentEpisodeMeta}</span>
                      </div>

                      <h1 className="truncate text-sm font-semibold tracking-tight sm:text-xl lg:text-3xl 2xl:text-4xl">
                        {show.title}
                      </h1>

                      <p className="mt-1 max-w-3xl text-[11px] leading-5 text-white/72 line-clamp-2 sm:mt-2 sm:text-sm sm:leading-6 sm:line-clamp-3 lg:text-base">
                        {currentEpisode?.description || show?.description || ""}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="absolute inset-0 z-10 flex items-center justify-center px-3 sm:px-6">
                  <div className="flex items-center gap-2 sm:gap-5 lg:gap-6">
                    <button
                      type="button"
                      onClick={() => seekBy(-10)}
                      className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md transition hover:scale-105 hover:bg-white/15 sm:h-14 sm:w-14 lg:h-16 lg:w-16"
                    >
                      <FaBackward className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                    </button>

                    <button
                      type="button"
                      onClick={togglePlay}
                      className="grid h-14 w-14 place-items-center rounded-full bg-white text-black shadow-2xl transition hover:scale-105 sm:h-20 sm:w-20 lg:h-24 lg:w-24"
                    >
                      {isPlaying ? (
                        <FaPause className="h-5 w-5 sm:h-8 sm:w-8 lg:h-9 lg:w-9" />
                      ) : (
                        <FaPlay className="ml-1 h-5 w-5 sm:h-8 sm:w-8 lg:h-9 lg:w-9" />
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => seekBy(10)}
                      className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md transition hover:scale-105 hover:bg-white/15 sm:h-14 sm:w-14 lg:h-16 lg:w-16"
                    >
                      <FaForward className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                    </button>
                  </div>
                </div>

                <div className="absolute inset-x-0 bottom-0 z-20 px-3 pb-3 sm:px-4 sm:pb-4 lg:px-6 lg:pb-6">
                  <div className="mx-auto w-full max-w-[calc(100%-8px)]">
                    <div className="mb-3 sm:mb-4">
                      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-white/16 sm:h-2">
                        <div
                          className="absolute inset-y-0 left-0 rounded-full bg-white/25"
                          style={{ width: `${bufferedPercent}%` }}
                        />
                        <div
                          className="absolute inset-y-0 left-0 rounded-full transition-[background-image,width] duration-700 ease-in-out"
                          style={{
                            width: `${progressPercent}%`,
                            backgroundImage:
                              "linear-gradient(90deg, var(--accent-from) 0%, var(--accent-mid) 45%, var(--accent-to) 100%)",
                          }}
                        />
                        <input
                          type="range"
                          min={0}
                          max={duration || 0}
                          step={0.1}
                          value={currentTime}
                          onMouseDown={() => setIsSeeking(true)}
                          onMouseUp={() => setIsSeeking(false)}
                          onTouchStart={() => setIsSeeking(true)}
                          onTouchEnd={() => setIsSeeking(false)}
                          onChange={(e) => {
                            const next = Number(e.target.value);
                            setCurrentTime(next);
                            if (videoRef.current) {
                              videoRef.current.currentTime = next;
                            }
                          }}
                          className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white"
                        />
                      </div>

                      <div className="mt-2 flex items-center justify-between text-[10px] text-white/68 sm:text-xs lg:text-sm">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <button
                          type="button"
                          onClick={togglePlay}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-md transition hover:bg-white/15 sm:h-10 sm:w-10 lg:h-11 lg:w-11"
                        >
                          {isPlaying ? (
                            <FaPause className="h-4 w-4 sm:h-4.5 sm:w-4.5 lg:h-5 lg:w-5" />
                          ) : (
                            <FaPlay className="ml-0.5 h-4 w-4 sm:h-4.5 sm:w-4.5 lg:h-5 lg:w-5" />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => seekBy(-10)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-md transition hover:bg-white/15 sm:h-10 sm:w-10 lg:h-11 lg:w-11"
                        >
                          <FaBackward className="h-4 w-4 sm:h-4.5 sm:w-4.5 lg:h-5 lg:w-5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => seekBy(10)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-md transition hover:bg-white/15 sm:h-10 sm:w-10 lg:h-11 lg:w-11"
                        >
                          <FaForward className="h-4 w-4 sm:h-4.5 sm:w-4.5 lg:h-5 lg:w-5" />
                        </button>

                        <button
                          type="button"
                          onClick={toggleMute}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-md transition hover:bg-white/15 sm:h-10 sm:w-10 lg:hidden"
                        >
                          {isMuted || volume === 0 ? (
                            <FaVolumeMute className="h-4 w-4" />
                          ) : (
                            <FaVolumeUp className="h-4 w-4" />
                          )}
                        </button>

                        <div
                          className="relative hidden items-center gap-2 sm:flex"
                          onMouseEnter={() => setShowVolumeSlider(true)}
                          onMouseLeave={() => setShowVolumeSlider(false)}
                        >
                          <button
                            type="button"
                            onClick={toggleMute}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-md transition hover:bg-white/15 lg:h-11 lg:w-11"
                          >
                            {isMuted || volume === 0 ? (
                              <FaVolumeMute className="h-4.5 w-4.5 lg:h-5 lg:w-5" />
                            ) : (
                              <FaVolumeUp className="h-4.5 w-4.5 lg:h-5 lg:w-5" />
                            )}
                          </button>

                          <div
                            className={`overflow-hidden transition-all duration-200 ${
                              showVolumeSlider
                                ? "w-28 opacity-100"
                                : "w-0 opacity-0"
                            }`}
                          >
                            <input
                              type="range"
                              min={0}
                              max={1}
                              step={0.01}
                              value={isMuted ? 0 : volume}
                              onChange={(e) =>
                                handleVolumeChange(Number(e.target.value))
                              }
                              className="h-1.5 w-28 cursor-pointer appearance-none rounded-full bg-white/15 accent-white"
                            />
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setCaptionsEnabled((prev) => !prev)}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-full border px-3 text-white backdrop-blur-md transition-[background-color,border-color] duration-500 sm:h-10 lg:h-11 lg:px-4"
                          style={
                            captionsEnabled
                              ? {
                                  backgroundColor: "var(--accent-soft)",
                                  borderColor: "var(--accent-border)",
                                }
                              : {
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                  borderColor: "rgba(255,255,255,0.15)",
                                }
                          }
                        >
                          <FaClosedCaptioning className="h-4 w-4" />
                          <span className="text-[11px] font-medium sm:text-xs lg:text-sm">
                            CC
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setShowInfo((prev) => !prev);
                            setShowSettings(false);
                            setShowLibraryRail(false);
                          }}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 backdrop-blur-md transition hover:bg-white/15 sm:h-10 lg:h-11 lg:px-4"
                        >
                          <FaInfoCircle className="h-4 w-4" />
                          <span className="text-[11px] font-medium sm:text-xs lg:text-sm">
                            Info
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setShowLibraryRail((prev) => !prev);
                            setShowSettings(false);
                            setShowInfo(false);
                          }}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 backdrop-blur-md transition hover:bg-white/15 sm:h-10 lg:h-11 lg:px-4"
                        >
                          <FaList className="h-4 w-4" />
                          <span className="text-[11px] font-medium sm:text-xs lg:text-sm">
                            Browse
                          </span>
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 xl:justify-end">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setShowSettings((prev) => !prev);
                              setShowInfo(false);
                              setShowLibraryRail(false);
                            }}
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 backdrop-blur-md transition hover:bg-white/15 sm:h-10 lg:h-11 lg:px-4"
                          >
                            <FaCog className="h-4 w-4" />
                            <span className="text-[11px] font-medium sm:text-xs lg:text-sm">
                              Settings
                            </span>
                          </button>

                          {showSettings ? (
                            <div className="absolute bottom-12 right-0 z-50 w-[min(88vw,320px)] rounded-3xl border border-white/10 bg-[#12091f]/94 p-4 shadow-2xl backdrop-blur-xl sm:bottom-14 sm:w-80">
                              <div className="mb-3 text-xs uppercase tracking-[0.26em] text-white/45">
                                Playback
                              </div>

                              <div className="mb-4">
                                <div className="mb-2 text-sm text-white/80">
                                  Speed
                                </div>

                                <div className="grid grid-cols-3 gap-2">
                                  {[0.5, 1, 1.25, 1.5, 1.75, 2].map((speed) => (
                                    <button
                                      key={speed}
                                      type="button"
                                      onClick={() => {
                                        setPlaybackRate(speed);
                                        if (videoRef.current) {
                                          videoRef.current.playbackRate = speed;
                                        }
                                      }}
                                      className={`rounded-2xl px-3 py-2 text-sm font-medium transition ${
                                        playbackRate === speed
                                          ? "bg-[#7d4bff] text-white"
                                          : "bg-white/8 text-white/85 hover:bg-white/14"
                                      }`}
                                    >
                                      {speed}x
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <div className="mb-2 text-sm text-white/80">
                                  Theme
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  {Object.entries(PLAYER_THEMES).map(
                                    ([key, item]) => (
                                      <button
                                        key={key}
                                        type="button"
                                        onClick={() => setThemeKey(key)}
                                        className={`flex items-center justify-between rounded-2xl px-3 py-2 text-sm transition ${
                                          themeKey === key
                                            ? "bg-white/14 text-white"
                                            : "bg-white/8 text-white/80 hover:bg-white/12"
                                        }`}
                                      >
                                        <span>{item.name}</span>
                                        {themeKey === key ? (
                                          <FaCheck className="h-3 w-3" />
                                        ) : null}
                                      </button>
                                    ),
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>

                        <button
                          type="button"
                          disabled={!previousEpisode}
                          onClick={goToPreviousEpisode}
                          className={`inline-flex h-9 items-center justify-center gap-2 rounded-full border px-3 backdrop-blur-md transition sm:h-10 lg:h-11 lg:px-4 ${
                            previousEpisode
                              ? "border-white/15 bg-white/10 hover:bg-white/15"
                              : "cursor-not-allowed border-white/8 bg-white/5 text-white/35"
                          }`}
                        >
                          <FaChevronLeft className="h-4 w-4" />
                          <span className="text-[11px] font-medium sm:text-xs lg:text-sm">
                            Prev
                          </span>
                        </button>

                        <button
                          type="button"
                          disabled={!nextEpisode}
                          onClick={goToNextEpisode}
                          className={`inline-flex h-9 items-center justify-center gap-2 rounded-full border px-3 backdrop-blur-md transition sm:h-10 lg:h-11 lg:px-4 ${
                            nextEpisode
                              ? "border-white/15 bg-white/10 hover:bg-white/15"
                              : "cursor-not-allowed border-white/8 bg-white/5 text-white/35"
                          }`}
                        >
                          <span className="text-[11px] font-medium sm:text-xs lg:text-sm">
                            Next
                          </span>
                          <FaChevronRight className="h-4 w-4" />
                        </button>

                        <button
                          type="button"
                          onClick={toggleFullscreen}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-md transition hover:bg-white/15 sm:h-10 sm:w-10 lg:h-11 lg:w-11"
                        >
                          {isFullscreen ? (
                            <FaCompress className="h-4 w-4 sm:h-4.5 sm:w-4.5 lg:h-5 lg:w-5" />
                          ) : (
                            <FaExpand className="h-4 w-4 sm:h-4.5 sm:w-4.5 lg:h-5 lg:w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {showInfo ? (
                <div className="absolute left-3 right-3 top-16 z-40 rounded-3xl border border-white/10 bg-[#0f0c18]/88 p-4 shadow-2xl backdrop-blur-xl sm:left-6 sm:right-auto sm:top-24 sm:w-[380px] sm:p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="mb-2 text-xs uppercase tracking-[0.26em] text-white/45">
                        Now Watching
                      </div>
                      <h2 className="truncate text-base font-semibold sm:text-xl">
                        {show.title}
                      </h2>
                      <div className="mt-1 text-sm text-white/65">
                        {currentEpisodeMeta}
                        {currentEpisode?.duration
                          ? ` • ${currentEpisode.duration}`
                          : ""}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowInfo(false)}
                      className="text-white/70 hover:text-white"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  <p className="mt-4 text-sm leading-6 text-white/78">
                    {currentEpisode?.description || show?.description || ""}
                  </p>

                  <div className="mt-4 grid grid-cols-2 gap-2 text-sm text-white/72">
                    <div className="rounded-2xl bg-white/6 px-3 py-2">
                      Speed: {playbackRate}x
                    </div>
                    <div className="rounded-2xl bg-white/6 px-3 py-2">
                      Theme: {theme.name}
                    </div>
                    <div className="rounded-2xl bg-white/6 px-3 py-2">
                      Captions: {captionsEnabled ? "On" : "Off"}
                    </div>
                    <div className="rounded-2xl bg-white/6 px-3 py-2">
                      Remaining: {formatTime(remainingTime)}
                    </div>
                  </div>
                </div>
              ) : null}

              {showLibraryRail ? (
                <div className="absolute inset-x-3 top-16 z-40 max-h-[72vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#0f0c18]/88 p-4 shadow-2xl backdrop-blur-xl sm:left-auto sm:right-6 sm:top-24 sm:w-[410px] sm:inset-x-auto">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="flex flex-1 items-center gap-2 rounded-2xl bg-white/6 p-1">
                      <button
                        type="button"
                        onClick={() => setRailTab("episodes")}
                        className={`flex-1 rounded-xl px-3 py-2 text-xs font-medium transition sm:text-sm ${
                          railTab === "episodes"
                            ? "bg-white text-black"
                            : "text-white/75 hover:text-white"
                        }`}
                      >
                        Episodes
                      </button>

                      <button
                        type="button"
                        onClick={() => setRailTab("related")}
                        className={`flex-1 rounded-xl px-3 py-2 text-xs font-medium transition sm:text-sm ${
                          railTab === "related"
                            ? "bg-white text-black"
                            : "text-white/75 hover:text-white"
                        }`}
                      >
                        You May Also Like
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowLibraryRail(false)}
                      className="text-white/70 hover:text-white"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  {railTab === "episodes" ? (
                    <>
                      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
                        {seasons.map((season, index) => (
                          <button
                            key={season.id}
                            type="button"
                            onClick={() => setCurrentSeasonIndex(index)}
                            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                              currentSeasonIndex === index
                                ? "bg-white text-black"
                                : "bg-white/8 text-white hover:bg-white/14"
                            }`}
                          >
                            {formatSeasonDisplay(season)}
                          </button>
                        ))}
                      </div>

                      <div className="space-y-2">
                        {(currentSeason?.episodes || []).map((episode) => {
                          const active = currentEpisode?.id === episode.id;
                          const thumbUrl = episode.thumbnail_path
                            ? getPublicUrl(episode.thumbnail_path)
                            : "";

                          return (
                            <button
                              key={episode.id}
                              type="button"
                              onClick={() =>
                                playEpisode(episode, currentSeasonIndex, true)
                              }
                              className={`flex w-full items-center gap-3 rounded-2xl border p-2 text-left transition ${
                                active
                                  ? "border-white/20 bg-white/12"
                                  : "border-white/8 bg-white/5 hover:bg-white/10"
                              }`}
                            >
                              <img
                                src={
                                  thumbUrl ||
                                  showPosterUrl ||
                                  "https://via.placeholder.com/640x360?text=Episode"
                                }
                                alt={episode.title}
                                className="h-14 w-24 rounded-xl object-cover sm:h-16 sm:w-28"
                              />

                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-semibold text-white">
                                  {episode.episode_number}. {episode.title}
                                </div>
                                <div className="truncate text-xs text-white/70 sm:text-sm">
                                  {episode.description || "No description."}
                                </div>
                              </div>

                              {active ? (
                                <FaCheck className="h-4 w-4 shrink-0 text-white" />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      {otherShowsMapped.length ? (
                        otherShowsMapped.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => navigate(`/show/${item.id}`)}
                            className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-white/5 p-2 text-left transition hover:bg-white/10"
                          >
                            <img
                              src={
                                item.hero_image_url ||
                                "https://via.placeholder.com/640x360?text=Show"
                              }
                              alt={item.title}
                              className="h-14 w-24 rounded-xl object-cover sm:h-16 sm:w-28"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold text-white">
                                {item.title}
                              </div>
                              <div className="truncate text-xs text-white/70 sm:text-sm">
                                {item.description || "No description."}
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="rounded-2xl border border-white/8 bg-white/5 p-4 text-sm text-white/70">
                          No other shows yet.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : null}

              {showNextEpisodeCard && nextEpisode ? (
                <div className="absolute right-3 top-3 z-40 w-[min(88vw,370px)] rounded-3xl border border-white/10 bg-[#12091f]/90 p-4 text-white shadow-2xl backdrop-blur-xl sm:right-6 sm:top-6">
                  <div className="mb-3 text-xs uppercase tracking-[0.28em] text-white/48">
                    Up Next
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                    <img
                      src={
                        nextEpisode.thumbnail_path
                          ? getPublicUrl(nextEpisode.thumbnail_path)
                          : showPosterUrl ||
                            "https://via.placeholder.com/640x360?text=Next+Episode"
                      }
                      alt={nextEpisode.title}
                      className="h-28 w-full object-cover sm:h-32"
                    />
                  </div>

                  <div className="mt-4">
                    <div className="text-base font-semibold sm:text-lg">
                      {nextEpisode.title}
                    </div>
                    <div className="mt-1 text-sm text-white/65">
                      {nextEpisode.__seasonIndex === currentSeasonIndex
                        ? formatSeasonDisplay(currentSeason)
                        : formatSeasonDisplay(
                            seasons[nextEpisode.__seasonIndex] || null,
                          )}{" "}
                      • Episode {nextEpisode.episode_number || 1}
                    </div>
                    <div className="mt-3 text-sm text-white/82">
                      Starts in{" "}
                      <span className="font-semibold text-white">
                        {nextCountdown}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={goToNextEpisode}
                      className="flex-1 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-black transition hover:opacity-95"
                    >
                      Play now
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowNextEpisodeCard(false)}
                      className="rounded-full border border-white/15 bg-white/8 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/12"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-black px-4 text-center text-gray-400">
              No episode available.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
